import React, { createContext, useContext, useState, useCallback } from "react";

export const SUPPORTED_COUNTRIES = ["ES", "UY", "US", "AR", "BR"];
const STORAGE_KEY = "infocity_country";

// Maps a browser locale (e.g. "es-AR", "en-US", "pt-BR") to one of our
// supported markets — only used as a fallback when the visitor has never
// picked a country before. Ambiguous locales (e.g. plain "es") never guess.
function detectCountryFromLocale() {
  if (typeof navigator === "undefined") return null;
  const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const raw of langs || []) {
    if (!raw) continue;
    const l = raw.split("@")[0]; // strip POSIX-style variant suffixes (e.g. "en-US@posix")
    const parts = l.split("-");
    if (parts.length >= 2) {
      const region = parts[1].toUpperCase();
      if (SUPPORTED_COUNTRIES.includes(region)) return region;
    }
  }
  return null;
}

function resolveInitialCountry() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED_COUNTRIES.includes(saved)) return saved;
  return detectCountryFromLocale();
}

const CountryContext = createContext(null);

export function CountryProvider({ children }) {
  const [country, setCountryState] = useState(resolveInitialCountry);

  const setCountry = useCallback((code) => {
    setCountryState(code || null);
    if (code) localStorage.setItem(STORAGE_KEY, code);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <CountryContext.Provider value={{ country, setCountry }}>
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error("useCountry must be used inside CountryProvider");
  return ctx;
}
